import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const METRICS = [
  { key: "miles", view: "leaderboard_miles" },
  { key: "gym_time", view: "leaderboard_gym_time" },
  { key: "github_commits", view: "leaderboard_github_commits" },
  { key: "protein", view: "leaderboard_protein" },
  { key: "fiber", view: "leaderboard_fiber" },
  { key: "calories", view: "leaderboard_calories" },
  { key: "water", view: "leaderboard_water" },
  { key: "ate_healthy", view: "leaderboard_ate_healthy" },
  { key: "low_sugars", view: "leaderboard_low_sugars" },
  { key: "fasted", view: "leaderboard_fasted" },
  { key: "body_fat", view: "leaderboard_body_fat" },
];

interface UseLeaderboardPreloadProps {
  userId: string | null;
  userCity: string | null;
  userCountry: string | null;
  isPremium: boolean;
  blockedUsers: string[];
  userConnections: string[];
  isLoadingBlocked: boolean;
  isLoadingConnections: boolean;
}

/**
 * Preloads leaderboard data for all filters when user authenticates.
 * This improves UX by fetching data for all location filters (global, national, local, connections)
 * ahead of time, so switching between filters is instant.
 */
export const useLeaderboardPreload = ({
  userId,
  userCity,
  userCountry,
  isPremium,
  blockedUsers,
  userConnections,
  isLoadingBlocked,
  isLoadingConnections,
}: UseLeaderboardPreloadProps) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Only preload when user is authenticated and all dependencies are loaded
    if (!userId || isLoadingBlocked || isLoadingConnections) return;

    const preloadAllFilters = async () => {
      console.log('[LeaderboardPreload] Starting preload for all filters...');
      
      // Define all filter combinations to preload
      const filters: Array<{
        type: "global" | "national" | "local" | "connections";
        city?: string | null;
        country?: string | null;
      }> = [
        { type: "global" },
      ];

      // Only preload premium filters if user is premium
      if (isPremium) {
        if (userCountry) {
          filters.push({ type: "national", country: userCountry });
        }
        if (userCity && userCountry) {
          filters.push({ type: "local", city: userCity, country: userCountry });
        }
        filters.push({ type: "connections" });
      }

      // Preload all metrics for all applicable filters in parallel
      const preloadPromises = filters.flatMap(filter =>
        METRICS.map(async (metric) => {
          try {
            let query = supabase.from(metric.view as any).select("*");

            // Apply location filters
            if (filter.type === "local" && filter.city && filter.country) {
              query = query.eq("city", filter.city).eq("country", filter.country);
            } else if (filter.type === "national" && filter.country) {
              query = query.eq("country", filter.country);
            }

            const { data, error } = await query.limit(1000);
            if (error) throw error;

            // Filter out blocked users (deleted accounts already filtered by DB views)
            let filteredData = (data || []).filter((leader: any) =>
              !blockedUsers.includes(leader.id)
            );

            // Apply connections filter if needed
            if (filter.type === "connections") {
              filteredData = filteredData.filter((leader: any) =>
                userConnections.includes(leader.id)
              );
            }

            // Cache the result using queryClient
            queryClient.setQueryData(
              ["leaderboard", metric.key, filter.type, filter.city, filter.country, blockedUsers, userConnections],
              filteredData
            );

            console.log(`[LeaderboardPreload] Cached ${metric.key} for ${filter.type} filter (${filteredData.length} users)`);
          } catch (error) {
            console.error(`[LeaderboardPreload] Failed to preload ${metric.key} for ${filter.type}:`, error);
          }
        })
      );

      await Promise.all(preloadPromises);
      console.log('[LeaderboardPreload] Preload complete!');
    };

    preloadAllFilters();
  }, [userId, userCity, userCountry, isPremium, blockedUsers, userConnections, isLoadingBlocked, isLoadingConnections, queryClient]);
};
